import { Notification, NotificationFilled, Time, UserMultiple } from '@carbon/icons-react';
import { HeaderBox, HorizBox, Pad } from 'component/basics';
import { SubtleButton } from 'component/button';
import {
    colorDataVizCategory1,
    colorSurfaceBannerInfo,
    colorSurfaceBannerWarning,
    colorTextOnAccentColorBg,
    colorTextOnTagLight,
    colorTextWarning,
} from 'component/color';
import { spacings } from 'component/constants';
import { formatToDateRangeString } from 'component/date';
import { Heading, UtilityText } from 'component/text';
import { StyleSheet, View } from 'react-native';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export function TopicHeaderBox({ status, preHeading, title, content }) {
    return (
        <View role='banner'>
            <HeaderBox backgroundColor={colorSurfaceBannerInfo} horiz={spacings.lg} vert={spacings.xl2}>
                {status && status}
                <Pad size={spacings.xl} />
                {preHeading && (
                    <>
                        <UtilityText label={preHeading} />
                        <Pad size={spacings.sm} />
                    </>
                )}
                <Heading text={title} type='large' weight='medium' />
                <Pad size={spacings.xl} />
                {content && content}
            </HeaderBox>
        </View>
    );
}

export function TopHeaderBar({ tag, notificationSubscribed, notificationOnPress }) {
    return (
        <HorizBox spread>
            {tag && tag}
            <HorizBox>
                <MockTopicFollowButton notificationSubscribed={notificationSubscribed} onPress={notificationOnPress} />
            </HorizBox>
        </HorizBox>
    );
}

export function AskMeAnythingLiveStatus({ live = false }) {
    const s = topicTagStyle;
    const label: TLabel = live ? t('Upcoming') : t('Closed');

    return (
        <View style={s.tag}>
            <UtilityText color={colorTextOnTagLight} label={label} type='tiny' />
        </View>
    );
}

export function TopicLiveStatus({ live = false }) {
    const s = topicTagStyle;
    const label: TLabel = live ? t('Live') : t('Ended');

    return (
        <View style={s.tag}>
            {live && (
                <>
                    <View style={s.outerDot}>
                        <View style={s.dot} />
                    </View>
                    <Pad size={spacings.xxs} />
                </>
            )}
            <UtilityText color={colorTextOnTagLight} label={label} type='tiny' weight='medium' />
        </View>
    );
}

const topicTagStyle = StyleSheet.create({
    outerDot: {
        width: 14,
        height: 14,
        backgroundColor: `${colorDataVizCategory1}20` as any,
        borderRadius: '50%' as any,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: spacings.xxs,
    },
    dot: {
        width: 8,
        height: 8,
        backgroundColor: colorDataVizCategory1,
        borderRadius: '50%' as any,
    },
    tag: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        padding: spacings.xxs,
        paddingRight: 8,
        borderRadius: 100,
        backgroundColor: colorSurfaceBannerWarning,
    },
});

export function MockTopicFollowButton({ notificationSubscribed = false, onPress }) {
    const notificationIcon = notificationSubscribed ? NotificationFilled : Notification;
    const label: TLabel = notificationSubscribed ? t('Following') : t('Follow');
    return <SubtleButton color={colorTextOnAccentColorBg} icon={notificationIcon} label={label} onPress={onPress} />;
}

export function TopicParticipants({ count, startAt, endAt }) {
    const currentDate = new Date();
    const endDate = new Date(endAt);
    const topicIsActive = currentDate.getTime() < endDate.getTime();
    if (topicIsActive) {
        return <ActiveUsers count={count} />;
    } else {
        return <TopicDate date={formatToDateRangeString({ start: new Date(startAt), end: new Date(endAt), locale: 'fr-CA' })} />;
    }
}

export function ActiveUsers({ count = 0 }) {
    const formatParams = { count: count, singular: t('participant'), plural: t('participants') };
    return (
        <HorizBox>
            <UserMultiple style={participantsStyle.icon} />
            <UtilityText formatParams={formatParams} label={t('{count} {noun}')} type='tiny' weight='medium' />
        </HorizBox>
    );
}

export function TopicDate({ date }) {
    return (
        <HorizBox>
            <Time style={participantsStyle.icon} />
            <UtilityText text={date} type='tiny' weight='medium' />
        </HorizBox>
    );
}

const participantsStyle = StyleSheet.create({
    icon: {
        marginRight: spacings.xs,
    },
});
